export declare type EnvBaseType = {
  headerPre: string;
  storageAuthPre: string;
  storageHeaderPre: string;
  extKeys: string[];
};

export const EnvBase: EnvBaseType = {
  headerPre: 'X-Super-With-',
  storageAuthPre: 'BizAuth-',
  storageHeaderPre: 'AdminAuth-',
  extKeys: [
    'NO-CACHE',
    'APP',
    'APP_CODE',
    'PLATFORM',
    'version',
    'FORCE',
    'OPERATOR_ID',
    'CASHIER_ID',
  ],
};

export const AppNames: AppNameType[] = ['admin', 'tenant', 'merchant', 'consumer', 'api', 'home'];
export const EnvNames: EnvNameType[] = ['local', 'wlan', 'test', 'dev', 'release', 'master'];

export const DefaultAppName: AppNameType = 'admin';
export const DefaultEnvName: EnvNameType = 'dev';

export const getAppName = (appName: string): AppNameType => {
  if (!AppNames.includes(appName as AppNameType)) {
    return 'admin';
  }
  return appName as AppNameType;
};

export const getEnvName = (envName: string): EnvNameType => {
  if (!EnvNames.includes(envName as EnvNameType)) {
    return 'dev';
  }
  return envName as EnvNameType;
};

export declare type EnvSettingType = {
  baseURL: string;
  staticHost: string;
  ip: string;
  port: number;
  timeout: number;
  staticEnv: string;
  aesKey: string;
  aesIv: string;
};

export declare type EnvSettingListType = {
  [key in EnvNameType]: EnvSettingType;
};

export const EnvSettingList: EnvSettingListType = {
  local: {
    baseURL: 'https://sinograin-back-local.mallx.net',
    staticHost: 'https://sup-st-local.mallx.net',
    staticEnv: 'local',
    ip: '127.0.0.1',
    port: 22010,
    timeout: 5000,
    aesKey: 'e62BXKYTCUYk3TJRx2nvRMEutRksvAx5',
    aesIv: 'KuYeVt3ESvuAUkff',
  },
  wlan: {
    baseURL: 'https://sinograin-back-wlan.mallx.net',
    staticHost: 'https://sup-st-wlan.mallx.net',
    staticEnv: 'wlan',
    ip: '192.168.0.100',
    port: 22030,
    timeout: 5000,
    aesKey: 'e62BXKYTCUYk3TJRx2nvRMEutRksvAx5',
    aesIv: 'KuYeVt3ESvuAUkff',
  },
  dev: {
    baseURL: 'https://sinograin-back-dev.mallx.net',
    // staticHost: 'https://sup-st-dev.mallx.net',
    staticHost: 'https://sup-st-local.mallx.net',
    staticEnv: 'dev',
    ip: '47.102.40.240',
    port: 22040,
    timeout: 5000,
    aesKey: 'e62BXKYTCUYk3TJRx2nvRMEutRksvAx5',
    aesIv: 'KuYeVt3ESvuAUkff',
  },
  test: {
    baseURL: 'https://sinograin-back-test.mallx.net',
    staticHost: 'https://sup-st-test.mallx.net',
    staticEnv: 'test',
    ip: '106.75.233.22',
    port: 22050,
    timeout: 5000,
    aesKey: 'e62BXKYTCUYk3TJRx2nvRMEutRksvAx5',
    aesIv: 'KuYeVt3ESvuAUkff',
  },
  release: {
    baseURL: 'https://sinograin-back-uat.mallx.net',
    staticHost: 'https://sup-st-uat.mallx.net',
    staticEnv: 'release',
    ip: '106.75.233.22',
    port: 22060,
    timeout: 5000,
    aesKey: 'e62BXKYTCUYk3TJRx2nvRMEutRksvAx5',
    aesIv: 'KuYeVt3ESvuAUkff',
  },
  master: {
    baseURL: 'https://api.zclzhbc.com',
    staticHost: 'https://static.zclzhbc.com',
    staticEnv: 'product',
    ip: '106.75.233.22',
    port: 22070,
    timeout: 5000,
    aesKey: 'e62BXKYTCUYk3TJRx2nvRMEutRksvAx5',
    aesIv: 'KuYeVt3ESvuAUkff',
  },
};

export declare type AppSettingType = {
  keys: string[];
  api: string;
};

export declare type AppSettingListType = {
  [key in AppNameType]: AppSettingType;
};

export const AppSettingList: AppSettingListType = {
  admin: {
    keys: ['SID', 'OSID', 'STID', 'SPID'],
    api: 'AdminApi',
  },
  tenant: {
    keys: ['TSID', 'TOSID', 'TSTID', 'TSPID'],
    api: 'TenantApi',
  },
  merchant: {
    keys: ['MSID', 'MOSID', 'MSTID', 'MSPID'],
    api: 'MerchantApi',
  },
  consumer: {
    keys: ['CSID', 'COSID', 'CSTID', 'CSPID'],
    api: 'ConsumerApi',
  },
  api: {
    keys: ['ASID', 'AOSID', 'ASTID', 'ASPID'],
    api: 'Api',
  },
  home: {
    keys: ['ASID', 'AOSID', 'ASTID', 'ASPID'],
    api: 'Home',
  },
};

const allApi: string = 'SUP-Super';

export declare type AppEnvType = {
  app: string;
  env: string;
  channel: string;
  apiUrl: string;
} & AppSettingType &
  EnvSettingType;

export declare type AppEnvListType = {
  [key in AppNameType]: {
    [key in EnvNameType]: AppEnvType;
  };
};

const BuildAppEnvList = (): AppEnvListType => {
  let appEnvList: AppEnvListType = {} as AppEnvListType;
  for (let appName of AppNames) {
    const appSetting: AppSettingType = AppSettingList[appName];
    // @ts-ignore
    appEnvList[appName] = {};
    for (let envName of EnvNames) {
      const envSetting: EnvSettingType = EnvSettingList[envName];
      appEnvList[appName][envName] = {
        app: appName,
        env: envName,
        channel: `${appName.toUpperCase()}-${envName.toUpperCase()}`,
        apiUrl: `http://${envSetting.ip}:${envSetting.port}/v3/api-docs/${appSetting.api}`,
        ...appSetting,
        ...envSetting,
      };
    }
  }
  return appEnvList;
};

export const AppEnvs: AppEnvListType = BuildAppEnvList();

export const getAppEnv = (appName: AppNameType, envName: EnvNameType): AppEnvType => {
  if (AppEnvs == null || AppEnvs[appName] == null || AppEnvs[appName][envName] == null) {
    return AppEnvs['admin']['dev'];
  }
  if (AppEnvs[appName][envName] == null) {
    return AppEnvs[appName]['dev'];
  }
  return AppEnvs[appName][envName];
};

export default AppEnvs;
